import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import CardHeader from '@mui/material/CardHeader'
import CardContent from '@mui/material/CardContent'
import AddIcon from '@mui/icons-material/Add';
import Button from "@mui/material/Button";
import {Alert, Box, Divider, Skeleton, Stack, Typography, useTheme} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SolicitationPreview from './SolicitationPreview'
import {useContext, useEffect, useState} from "react";
import {HttpSolicitation} from "../../http/solicitation/httpSolicitation";
import {FilterSolicitationSearch, FilterSolicitationSearchFields} from "../../types/solicitation/solicitationFilter";
import {Channels} from "../../types/generalData";
import {EntityListWithPagination, EntityListWithPaginationFields} from "../../types/baseEntities";
import {Solicitation, SolicitationFields} from 'src/types/solicitation/solicitationData';
import {CompanyContext, CompanyContextValue} from "../solicitation-new/context/companyContext";
import {SolicitudPreviewEstados} from "./dummyTypes";
import {Icon} from "@iconify/react";
import {HttpClientUser} from "../../http/client/httpClientUser";
import {ClientUserDTOFields} from "../../types/client/clientData";
import CompanyCuitSyncDialog from "../../@core/layouts/components/horizontal/app-bar-content/CompanyCuitSyncDialog";

const defaultFilter: FilterSolicitationSearch = {
    [FilterSolicitationSearchFields.SolicitationId]: undefined,
    [FilterSolicitationSearchFields.ClientId]: -1,
    [FilterSolicitationSearchFields.BusinessName]: '',
    [FilterSolicitationSearchFields.CUIT]: '',
    [FilterSolicitationSearchFields.SolicitationOriginId]: undefined,
    [FilterSolicitationSearchFields.OperationOriginId]: undefined,
    [FilterSolicitationSearchFields.BankCode]: undefined,
    [FilterSolicitationSearchFields.BranchCode]: undefined,
    [FilterSolicitationSearchFields.SolicitationStateCode]: undefined,
    [FilterSolicitationSearchFields.CommercialId]: undefined,
    [FilterSolicitationSearchFields.RegisterDate]: undefined,
    [FilterSolicitationSearchFields.SolicitationChannelCode]: Channels.Onboarding,
    [FilterSolicitationSearchFields.SolicitationStateClassificationListCode]: undefined,
    [FilterSolicitationSearchFields.SolicitationTypeCode]: undefined
}

const Home = () => {
    const navigate = useNavigate()
    const theme = useTheme()
    const { clientId, setClientSolicitationId, setClientId } = useContext<CompanyContextValue>(CompanyContext)
    
    const [loading, setLoading] = useState<boolean>(false)
    const [filteredSolicitations, setFilteredSolicitations] = useState<EntityListWithPagination<Solicitation>>()
    const [openNewCompany, setOpenNewCompany] = useState<boolean>(false)
    
    const onCloseNewCompany = () => setOpenNewCompany(false)

    useEffect(() => {
        const filter: FilterSolicitationSearch = {
            ...defaultFilter,
            [FilterSolicitationSearchFields.ClientId]: clientId,
        }
        if (clientId) {
            setLoading(true)
            setFilteredSolicitations(undefined)
            HttpSolicitation.getSolicitations(filter).then((r) => {
                setFilteredSolicitations(r)
                setLoading(false)
            })
        } else {
            HttpClientUser.getCompanies()
                .then(companies => {
                    if (companies.length !== 0) setClientId(companies?.[0][ClientUserDTOFields.ClientId])
                    else setOpenNewCompany(true)
                })
        }
    }, [clientId])


    const pendingSolicitations = filteredSolicitations?.[EntityListWithPaginationFields.List].filter(
        (solicitud) => solicitud[SolicitationFields.SolicitationStateCode] === SolicitudPreviewEstados.Pendiente
    );
    const analyzingSolicitations = filteredSolicitations?.[EntityListWithPaginationFields.List].filter(
        (solicitud) => solicitud[SolicitationFields.SolicitationStateCode] === SolicitudPreviewEstados.Analisis
            || solicitud[SolicitationFields.SolicitationStateCode] === SolicitudPreviewEstados.EnAprobacion ||
            solicitud[SolicitationFields.SolicitationStateCode] === SolicitudPreviewEstados.PendienteMonetizacion ||
            solicitud[SolicitationFields.SolicitationStateCode] === SolicitudPreviewEstados.PendienteAprobacion
    );
    const approvedSolicitations = filteredSolicitations?.[EntityListWithPaginationFields.List].filter(
        (solicitud) => solicitud[SolicitationFields.SolicitationStateCode] === SolicitudPreviewEstados.Aprobada
        || solicitud[SolicitationFields.SolicitationStateCode] === SolicitudPreviewEstados.Instrumentada
    );
    const rejectedSolicitations = filteredSolicitations?.[EntityListWithPaginationFields.List].filter(
        (solicitud) => solicitud[SolicitationFields.SolicitationStateCode] === SolicitudPreviewEstados.Rechazada
        || solicitud[SolicitationFields.SolicitationStateCode] === SolicitudPreviewEstados.Anulada || 
            solicitud[SolicitationFields.SolicitationStateCode] === SolicitudPreviewEstados.Desistida
        || solicitud[SolicitationFields.SolicitationStateCode] === SolicitudPreviewEstados.DesistidaPorBanco
        || solicitud[SolicitationFields.SolicitationStateCode] === SolicitudPreviewEstados.NoAcordada
    );

    return (
        <Grid container spacing={6}>
            <Grid item xs={12}>
                <Card>
                    <CardHeader title='Solicitudes'
                        action={<Button color='primary' variant='contained' disabled={(!clientId || clientId === 0)}
                                        onClick={() => {
                            setClientSolicitationId(0)
                            localStorage.setItem('solicitationId', '0')
                            navigate('nueva-solicitud')
                        }} startIcon={<AddIcon />}
                        >
                            Nueva Solicitud
                        </Button>}
                    />
                    <Divider />
                    <CardContent sx={{ backgroundColor: theme.palette.background.default }} >
                        <Box sx={{ width: '100%', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                            <Grid container spacing={4}>
                                {
                                    loading &&
                                    <Grid container spacing={2}>
                                        {[...Array(4)].map((_, idx) => (
                                            <Grid item xs={3} key={idx}>
                                                <Skeleton height={320} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                }
                                {
                                    filteredSolicitations && (
                                        filteredSolicitations.lista.length !== 0 ?
                                            <Grid container spacing={2}>
                                                <Grid item xs={3}>
                                                    <Stack direction={'row'} alignItems={'center'} spacing={2} justifyContent={'center'} sx={{ '& svg': { mr: 1.8, color: `#FFB400` } }}>
                                                        <Icon icon='mdi:circle' fontSize='1rem' />
                                                        <Typography variant="h6" style={{ margin: '10px 0px' }}>
                                                            Pendientes
                                                        </Typography>
                                                    </Stack>
                                                    {pendingSolicitations?.map((solicitud, index) => (
                                                        <div key={index} style={{ marginBottom: '10px' }}>
                                                            <SolicitationPreview solicitud={solicitud} />
                                                        </div>
                                                    ))}
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Stack direction={'row'} alignItems={'center'} spacing={2} justifyContent={'center'} sx={{ '& svg': { mr: 1.8, color: `#29b6f6` } }}>
                                                        <Icon icon='mdi:circle' fontSize='1rem' />
                                                        <Typography variant="h6" style={{ margin: '10px 0px' }}>
                                                            En Analisis
                                                        </Typography>
                                                    </Stack>
                                                    {/* Mostramos las cartas de solicitud en an�lisis */}
                                                    {analyzingSolicitations?.map((solicitud, index) => (
                                                        <div key={index} style={{ marginBottom: '10px' }}>
                                                            <SolicitationPreview solicitud={solicitud} />
                                                        </div>
                                                    ))}
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Stack direction={'row'} alignItems={'center'} spacing={2} justifyContent={'center'} sx={{ '& svg': { mr: 1.8, color: `#66bb6a` } }}>
                                                        <Icon icon='mdi:circle' fontSize='1rem' />
                                                        <Typography variant="h6" style={{ margin: '10px 0px' }}>
                                                            Aprobadas
                                                        </Typography>
                                                    </Stack>
                                                    {approvedSolicitations?.map((solicitud, index) => (
                                                        <div key={index} style={{ marginBottom: '10px' }}>
                                                            <SolicitationPreview solicitud={solicitud} />
                                                        </div>
                                                    ))}
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Stack direction={'row'} alignItems={'center'} spacing={2} justifyContent={'center'} sx={{ '& svg': { mr: 1.8, color: `#f44336` } }}>
                                                        <Icon icon='mdi:circle' fontSize='1rem' />
                                                        <Typography variant="h6" style={{ margin: '10px 0px' }}>
                                                            Rechazadas
                                                        </Typography>
                                                    </Stack>
                                                    {rejectedSolicitations?.map((solicitud, index) => (
                                                        <div key={index} style={{ marginBottom: '10px' }}>
                                                            <SolicitationPreview solicitud={solicitud} />
                                                        </div>
                                                    ))}
                                                </Grid>
                                            </Grid>
                                        :
                                        <Grid item xs={12} textAlign={'center'}>
                                            <Alert severity={'info'}>No hay empresa seleccionada o no hay solicitudes registradas para esta empresa</Alert>
                                        </Grid>
                                    )
                                }
                                {
                                    !filteredSolicitations && !loading && (!clientId || clientId === 0) &&
                                    <Grid item xs={12} textAlign={'center'}>
                                        <Alert severity={'info'}>Seleccione o agregue una empresa para poder operar con las solicitudes</Alert>
                                    </Grid>
                                }
                            </Grid>
                        </Box>

                    </CardContent>
                </Card>
            </Grid>

            <CompanyCuitSyncDialog open={openNewCompany}
                                   onClose={onCloseNewCompany}
                                   subtitle={"Agregue la empresa a representar para comenzar a operar. Ingrese el CUIT de su empresa para traerlo de la base de datos de AFIP"}
                                   saveClient
            />

            {/*
                !clientId &&
                    <CompanySelectDialog open={open}
                                         onClose={() => { setOpen(false) }}
                    />
            */}
        </Grid>
    )
}

export default Home
